import { Clickable } from '@carvana/merch-components';
import { Fragment, useRef } from 'react';
import type {
  ContentModSection,
  ContentModSubSection,
  SeoContent
} from '~/contracts/srp/Seo';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import {
  ContentReadMoreAreaWrapper,
  ContentReadMoreOverview,
  ContentReadMoreSections,
  ContentReadMoreSectionTwo,
  ContentReadMoreSectionThree,
  ContentSectionOverviewText,
  ContentSectionTextBottom,
  ContentReadMoreSectionSeparatorTwo,
  ContentReadMoreSectionSeparatorFour,
  BannerWrapper,
  BannerText,
  StyledHalo
} from './ContentMod.styles';
import ContentPicture from './ContentPicture';
import { sortSectionPriority } from './utilities';

function checkDoSectionsExist(
  sections: ContentModSection[]
): sections is [ContentModSection, ContentModSection] {
  return sections.length === 2;
}

type OwnProps = {
  contentMods: SeoContent;
};

function Content({ contentMods: mods }: OwnProps) {
  const readMoreAreaRef = useRef<HTMLDivElement>();

  const onReadMoreClick = () => {
    if (readMoreAreaRef.current?.scrollIntoView) {
      readMoreAreaRef.current.scrollIntoView({
        block: 'start',
        behavior: 'smooth'
      });
    }
  };

  const orderedSections = mods.contentModsSections.sort(sortSectionPriority);
  const doSectionsExist = checkDoSectionsExist(orderedSections);

  const firstSubSections: ContentModSubSection[] = doSectionsExist
    ? orderedSections[0].subSections.sort(sortSectionPriority)
    : [];

  const secondSubSections: ContentModSubSection[] = doSectionsExist
    ? orderedSections[1].subSections.sort(sortSectionPriority)
    : [];

  const contentModExists = doSectionsExist && mods.contentMod;
  const imageText = (contentModExists && mods.contentMod.imageText) || '';

  const [imageTextFirst, imageTextLast] = imageText.split('Read more');

  return contentModExists ? (
    <div className="w-full">
      <BannerWrapper>
        <BannerText>
          <StyledHalo />

          <h2 className="tl-brand-s">
            <div data-qa="SEO Header Text 1">
              {mods.contentMod.imageHeader1}
            </div>
            <div data-qa="SEO Header Text 2">
              {mods.contentMod.imageHeader2}
            </div>
          </h2>

          <Clickable
            element="p"
            className="tl-body-m l:tl-body-l"
            onClick={onReadMoreClick}
            data-analytics-id="ContentMods - Read More"
          >
            {imageTextFirst}
            <span className="underline">Read more</span>
            {imageTextLast}
          </Clickable>
        </BannerText>

        <ContentPicture contentMods={mods} />
      </BannerWrapper>

      <ContentReadMoreAreaWrapper
        ref={readMoreAreaRef}
        data-cv-test="Cv.Search.ContentMods.ReadMoreAreaWrapper"
      >
        <ContentReadMoreSections data-cv-test="Cv.Search.ContentMods.ReadMoreSections">
          <ContentReadMoreOverview data-cv-test="Cv.Search.ContentMods.Overview">
            <StyledHalo />

            <p data-cv-test="Cv.Search.ContentMods.OverviewHeader2">
              <span data-cv-test="Cv.Search.ContentMods.OverviewHeader1">
                {mods.contentMod.overviewHeader1}
              </span>
              {mods.contentMod.overviewHeader2}
            </p>

            <ContentSectionOverviewText data-cv-test="Cv.Search.ContentMods.OverviewText">
              {mods.contentMod.overviewText}
            </ContentSectionOverviewText>
          </ContentReadMoreOverview>

          {doSectionsExist ? (
            <>
              <ContentReadMoreSectionSeparatorTwo />

              <ContentReadMoreSectionTwo data-cv-test="Cv.Search.ContentMods.SectionTwo">
                <StyledHalo />

                <h3 data-cv-test="Cv.Search.ContentMods.SectionTwoHeader">
                  {orderedSections[0].sectionHeader}
                </h3>

                <ContentSectionTextBottom data-cv-test="Cv.Search.ContentMods.SectionTwoTextBottom">
                  {firstSubSections.map(subSection => (
                    <Fragment key={subSection.sectionHeader}>
                      <h3>{subSection.sectionHeader}</h3>
                      <span>{subSection.sectionText}</span>
                    </Fragment>
                  ))}
                </ContentSectionTextBottom>
              </ContentReadMoreSectionTwo>

              <ContentReadMoreSectionSeparatorFour />

              <ContentReadMoreSectionThree data-cv-test="Cv.Search.ContentMods.SectionThree">
                <StyledHalo />

                <h3 data-cv-test="Cv.Search.ContentMods.SectionThreeHeader">
                  {orderedSections[1].sectionHeader}
                </h3>

                <ContentSectionTextBottom data-cv-test="Cv.Search.ContentMods.SectionThreeTextBottom">
                  {secondSubSections.map(subSection => (
                    <Fragment key={subSection.sectionHeader}>
                      <h3>{subSection.sectionHeader}</h3>
                      <span>{subSection.sectionText}</span>
                    </Fragment>
                  ))}
                </ContentSectionTextBottom>
              </ContentReadMoreSectionThree>
            </>
          ) : null}
        </ContentReadMoreSections>
      </ContentReadMoreAreaWrapper>
    </div>
  ) : null;
}

export default withErrorBoundary(Content);
